<template>
  <div>
    <!-- FAB colapsable: pestaña al borde + botón de bug -->
    <div class="bug-report-fab" :class="{ 'is-collapsed': isCollapsed }">
      <!-- Pestaña con flechita para desplegar/ocultar -->
      <button
        class="bug-report-toggle"
        :title="isCollapsed ? 'Reportar error' : 'Ocultar'"
        :aria-label="isCollapsed ? 'Mostrar botón de reportar error' : 'Ocultar botón de reportar error'"
        @click="isCollapsed = !isCollapsed"
      >
        <svg class="bug-report-toggle__chevron" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <path :d="isCollapsed ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'"></path>
        </svg>
      </button>

      <!-- Botón flotante -->
      <button
        @click="openModal"
        class="bug-report-button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m8 2 1.88 1.88"></path>
          <path d="M14.12 3.88 16 2"></path>
          <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path>
          <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path>
          <path d="M12 20v-9"></path>
          <path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path>
          <path d="M6 13H2"></path>
          <path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path>
          <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path>
          <path d="M22 13h-4"></path>
          <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path>
        </svg>
      </button>
    </div>

    <!-- Modal del formulario -->
    <woot-modal v-model:show="showModal" :on-close="closeModal" size="medium">
      <div class="h-auto overflow-auto flex flex-col">
        <woot-modal-header
          :header-title="'Reportar Error'"
          class="centered-modal-header"
        />
        <form @submit.prevent="submitReport" class="flex flex-col p-6 space-y-4">
          <!-- Título del bug -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12 dark:text-n-slate-11">
              Título del Error
              <span class="text-n-ruby-9">*</span>
            </label>
            <input
              v-model="form.title"
              type="text"
              class="w-full px-3 py-2 border border-n-container rounded-lg text-sm bg-white dark:bg-n-solid-1 text-n-slate-12 dark:text-n-slate-11 placeholder-n-slate-9 dark:placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
              placeholder="Describe brevemente el error"
              required
              maxlength="255"
            />
          </div>

          <!-- Descripción -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12 dark:text-n-slate-11">
              Descripción del Error
              <span class="text-n-ruby-9">*</span>
            </label>
            <textarea
              v-model="form.description"
              class="w-full px-3 py-2 border border-n-container rounded-lg text-sm bg-white dark:bg-n-solid-1 text-n-slate-12 dark:text-n-slate-11 placeholder-n-slate-9 dark:placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent resize-vertical"
              placeholder="Describe detalladamente lo que sucedió"
              required
              maxlength="2000"
              rows="4"
            ></textarea>
          </div>

          <!-- Pasos para reproducir -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12 dark:text-n-slate-11">
              Pasos para Reproducir
            </label>
            <textarea
              v-model="form.steps_to_reproduce"
              class="w-full px-3 py-2 border border-n-container rounded-lg text-sm bg-white dark:bg-n-solid-1 text-n-slate-12 dark:text-n-slate-11 placeholder-n-slate-9 dark:placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent resize-vertical"
              placeholder="1. Primer paso&#10;2. Segundo paso&#10;3. Resultado esperado vs resultado actual"
              maxlength="1000"
              rows="3"
            ></textarea>
          </div>

          <!-- Archivo adjunto -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12 dark:text-n-slate-11">
              Captura de Pantalla o Archivo
            </label>
            <input
              ref="fileInput"
              type="file"
              @change="handleFileUpload"
              accept="image/*,.pdf,.txt,.log"
              class="w-full px-3 py-2 border-2 border-dashed border-n-container rounded-lg text-sm bg-white dark:bg-n-solid-1 text-n-slate-12 dark:text-n-slate-11 file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-n-brand file:text-white hover:file:bg-n-brand/90"
            />
            <div v-if="form.attachment" class="flex items-center gap-2 p-2 bg-n-alpha-1 dark:bg-n-solid-2 rounded-lg">
              <fluent-icon icon="attach" size="16" class="text-n-slate-9" />
              <span class="text-sm text-n-slate-11 flex-1">{{ form.attachment.name }}</span>
              <button 
                type="button" 
                @click="removeFile" 
                class="p-1 hover:bg-n-alpha-2 dark:hover:bg-n-solid-3 rounded text-n-slate-9 hover:text-n-slate-11"
              >
                <fluent-icon icon="dismiss" size="14" />
              </button>
            </div>
          </div>

          <!-- Información automática (solo visual) -->
          <div class="space-y-2">
            <div class="p-4 bg-n-alpha-1 dark:bg-n-solid-2 rounded-lg border border-n-container">
              <h4 class="text-sm font-medium text-n-slate-11 dark:text-n-slate-10 mb-3">Información del Sistema</h4>
              <div class="space-y-2 text-xs">
                <div class="flex">
                  <span class="font-medium text-n-slate-11 dark:text-n-slate-10 w-20">URL:</span>
                  <span class="text-n-slate-9 dark:text-n-slate-8 break-all">{{ currentUrl }}</span>
                </div>
                <div class="flex">
                  <span class="font-medium text-n-slate-11 dark:text-n-slate-10 w-20">Navegador:</span>
                  <span class="text-n-slate-9 dark:text-n-slate-8 break-all">{{ userAgent }}</span>
                </div>
                <div class="flex">
                  <span class="font-medium text-n-slate-11 dark:text-n-slate-10 w-20">Fecha:</span>
                  <span class="text-n-slate-9 dark:text-n-slate-8">{{ currentTime }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 pt-4 border-t border-n-container">
            <Button faded slate type="reset" :label="'Cancelar'" @click="closeModal" />
            <Button type="submit" :label="'Enviar Reporte'" :isLoading="isSubmitting" />
          </div>
        </form>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import BugReportsAPI from '../api/bugReports';
import { useAlert } from '../composables';

export default {
  name: 'BugReportButton',
  components: {
    Button,
  },
  mounted() {
  },
  data() {
    return {
      showModal: false,
      isCollapsed: true,
      isSubmitting: false,
      form: {
        title: '',
        description: '',
        steps_to_reproduce: '',
        attachment: null,
        current_url: '',
        user_agent: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    currentUrl() {
      return window.location.href;
    },
    userAgent() {
      return navigator.userAgent;
    },
    currentTime() {
      return new Date().toLocaleString();
    },
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        title: '',
        description: '',
        steps_to_reproduce: '',
        attachment: null,
        current_url: '',
        user_agent: '',
      };
      this.isSubmitting = false;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validar tamaño (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          useAlert('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }
        this.form.attachment = file;
      }
    },
    removeFile() {
      this.form.attachment = null;
      this.$refs.fileInput.value = '';
    },
    async submitReport() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = new FormData();
        
        // Agregar campos del formulario
        formData.append('bug_report[title]', this.form.title);
        formData.append('bug_report[description]', this.form.description);
        formData.append('bug_report[steps_to_reproduce]', this.form.steps_to_reproduce || '');
        formData.append('bug_report[current_url]', this.currentUrl);
        formData.append('bug_report[user_agent]', this.userAgent);

        // Agregar archivo adjunto si existe
        if (this.form.attachment) {
          formData.append('bug_report[attachment]', this.form.attachment);
        }

        // Enviar reporte a la API
        const response = await BugReportsAPI.create(formData);

        if (response.data.success) {
          useAlert('Reporte de error enviado correctamente.');
          this.closeModal();
        } else {
          throw new Error(response.data.message || 'Error al enviar el reporte');
        }
      } catch (error) {
        console.error('Error submitting bug report:', error);
        const errorMessage = error.response?.data?.message || 'Error al enviar el reporte. Inténtalo de nuevo.';
        useAlert(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
    validateForm() {
      if (!this.form.title.trim()) {
        useAlert('El título es obligatorio.');
        return false;
      }
      if (!this.form.description.trim()) {
        useAlert('La descripción es obligatoria.');
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Contenedor del FAB: pestaña + botón, anclado abajo a la derecha. */
.bug-report-fab {
  position: fixed;
  bottom: 20px;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.3s ease;

  /* Colapsado: se desliza al borde, dejando visible solo la pestaña */
  &.is-collapsed {
    /* ancho del botón (56) + gap (8) para que quede fuera de pantalla */
    transform: translateX(64px);
  }
}

/* Pestaña con flechita: media píldora pegada al borde */
.bug-report-toggle {
  flex-shrink: 0;
  width: 24px;
  height: 52px;
  padding: 0;
  border: none;
  border-radius: 16px 0 0 16px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* halo interno para que la solapa no se lea como un rectángulo plano */
  box-shadow:
    -4px 2px 16px rgba(0, 0, 0, 0.24),
    inset 1px 0 0 rgba(255, 255, 255, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  transition:
    width 0.2s ease,
    filter 0.2s ease,
    box-shadow 0.2s ease;

  @apply bg-n-brand;

  &__chevron {
    /* compensa el lado recto: el ícono queda óptico al centro */
    margin-right: 1px;
    transition: transform 0.2s ease;
  }

  &:hover {
    width: 28px;
    filter: brightness(1.08);

    .bug-report-toggle__chevron {
      transform: translateX(-1px);
    }
  }

  &:active {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: -3px;
  }

  .dark & {
    box-shadow:
      -4px 2px 18px rgba(0, 0, 0, 0.5),
      inset 1px 0 0 rgba(255, 255, 255, 0.16),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
}

/* Colapsado: la solapa es el único asidero, se hace más notoria */
.bug-report-fab.is-collapsed .bug-report-toggle {
  width: 26px;

  &:hover {
    width: 30px;
  }
}

.bug-report-button {
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);
  transition:
    transform 0.2s ease,
    filter 0.2s ease,
    box-shadow 0.2s ease;
  color: white;

  @apply bg-n-brand;

  &:hover {
    filter: brightness(1.08);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.97);
  }

  .dark & {
    box-shadow:
      0 6px 18px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
  }
}

/* Centrar el título del modal */
:deep(.centered-modal-header) {
  align-items: center !important;
  text-align: center;

  h2 {
    text-align: center;
    width: 100%;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .bug-report-fab {
    right: 0;
    bottom: 16px;

    &.is-collapsed {
      /* botón 48 + gap 8 */
      transform: translateX(56px);
    }
  }

  .bug-report-button {
    width: 48px;
    height: 48px;
  }
}
</style>